import { createFileRoute } from '@tanstack/react-router'
import { Suspense } from 'react'
import { cn, formatDateLocale } from '@/lib/utils'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import { ExternalLinkIcon, CreditCardIcon, TrendingUpIcon, CalendarIcon, CheckIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from './-components/shared/skeleton'
import { useAppForm } from '@/lib/use-app-form'
import * as z from 'zod'
import { queryClient, trpc } from '@/main'
import { useTranslation } from '@/lib/locales'
import { Spinner } from '@/components/ui/spinner'
import { SettingsSection } from './-components/settings/settings-section'
import { SettingsSectionSkeleton } from './-components/app-layout-skeleton'
import { InfoPopover } from '@/components/custom/info-popover'

// Constant locale + currency, so build the formatter once instead of on every render.
const eurFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' })

export const Route = createFileRoute('/_app/account/billing')({
  loader: ({ context }) => {
    context.queryClient.prefetchQuery(trpc.billing.get.queryOptions())
  },
  component: RouteComponent,
  pendingComponent: BillingPending,
  staticData: {
    pageTitle: 'Billing',
    pageIcon: CreditCardIcon,
  },
})

function BillingPending() {
  return (
    <div className="flex flex-col gap-6">
      <BillingPlanSkeleton />
      <SettingsSectionSkeleton rows={6} />
    </div>
  )
}

const getStatusColor = (status: string | undefined, canceled: boolean | undefined) => {
  if (canceled || status === 'canceled') return 'bg-destructive text-destructive-foreground'
  if (status === 'active') return 'bg-emerald-500 text-white'
  return 'bg-amber-500 text-white'
}

function RouteComponent() {
  const { t } = useLingui()
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const { mutateAsync: updateUser } = useMutation(trpc.auth.updateUser.mutationOptions())

  // Defined inside the component (like every other form route) so the zod import lands in this
  // route's lazy component chunk, not the eager route-definition bundle. A module-level zod schema
  // here was the sole thing dragging zod's runtime (~72 kB) into the main entry chunk.
  // All billing fields are required (they drive the "*" marker and block save when empty) except the
  // postal code, which stays optional.
  const form = useAppForm({
    validators: {
      onChange: z.object({
        billingEmail: z.email(),
        billingName: z.string().min(1),
        nif: z.string().min(1),
        billingCountry: z.string().min(1),
        billingProvince: z.string().min(1),
        billingCity: z.string().min(1),
        address: z.string().min(1),
        // Postal code is the one optional field: a plain string with no `.min(1)`, so an empty value
        // passes and never trips the "Required" / aria-invalid state (defaultValues coerce null→'').
        cp: z.string(),
      }),
    },
    defaultValues: {
      billingEmail: user?.billingEmail ?? '',
      billingName: user?.billingName ?? '',
      nif: user?.nif ?? '',
      billingCountry: user?.billingCountry ?? '',
      billingProvince: user?.billingProvince ?? '',
      billingCity: user?.billingCity ?? '',
      address: user?.address ?? '',
      cp: user?.cp ?? '',
    },
    onSubmit: async ({ value }) => {
      await updateUser(value)
      queryClient.invalidateQueries(trpc.auth.getUser.queryFilter())
    },
  })

  return (
    <div className="flex flex-col gap-6">
      <Suspense fallback={<BillingPlanSkeleton />}>
        <BillingPlan />
      </Suspense>

      {/* Billing Details Form */}
      <SettingsSection title={t`Billing information`} description={t`Your billing address and tax information`}>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            event.stopPropagation()
            form.handleSubmit()
          }}
          className="flex flex-col gap-2 **:data-[slot=form-label]:w-56"
        >
          <form.AppField
            name="billingEmail"
            children={(field) => <field.TextField label={t`Billing email`} type="email" orientation="horizontal" />}
          />
          <form.AppField
            name="billingName"
            children={(field) => <field.TextField label={t`Name or company name`} orientation="horizontal" />}
          />
          <form.AppField
            name="nif"
            children={(field) => <field.TextField label={t`NIF, VAT, TAX ID...`} orientation="horizontal" />}
          />
          <form.AppField
            name="billingCountry"
            listeners={{ onChange: () => form.setFieldValue('billingProvince', '') }}
            children={(field) => <field.CountryField label={t`Country`} orientation="horizontal" />}
          />
          <form.Subscribe selector={(state) => state.values.billingCountry}>
            {(country) => (
              <form.AppField
                name="billingProvince"
                children={(field) => (
                  <field.ProvinceField label={t`State / Province`} orientation="horizontal" country={country} />
                )}
              />
            )}
          </form.Subscribe>
          <form.AppField
            name="billingCity"
            children={(field) => <field.TextField label={t`City`} orientation="horizontal" />}
          />
          <form.AppField
            name="address"
            children={(field) => <field.TextField label={t`Address`} orientation="horizontal" />}
          />
          <form.AppField
            name="cp"
            children={(field) => <field.TextField label={t`Postal code`} orientation="horizontal" />}
          />

          <div className="mt-2 flex">
            <form.AppForm>
              <form.SubmitButton label={t`Save changes`} />
            </form.AppForm>
          </div>
        </form>
      </SettingsSection>
    </div>
  )
}

function BillingPlan() {
  const { t } = useLingui()
  const { language } = useTranslation()
  const { data: customer } = useSuspenseQuery(trpc.billing.get.queryOptions())
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const { mutateAsync: retrieveBillingIntent, isPending } = useMutation(trpc.billing.retrieveIntent.mutationOptions())
  const { mutateAsync: retrieveBillingPortal, isPending: isPendingPortal } = useMutation(
    trpc.billing.retrievePortal.mutationOptions(),
  )

  // Builds the plan's feature list from the plan's structured fields (account type + monthly limits;
  // a limit of -1 means unlimited). Previously the server pre-rendered this localized bullet string and
  // returned it as `plan.description`; the copy now lives here so the backend stays language-agnostic.
  const planDescription = (plan: (typeof customer.plans)[number]): string => {
    const lines: string[] = []
    if (user?.type === 'applicant' && plan.accountType) {
      lines.push(plan.accountType === 'advanced' ? t`Advanced report of your profile` : t`Basic report of your profile`)
    }
    const products = plan.limitProduct ?? ''
    lines.push(
      plan.limitProduct === -1
        ? t`Unlimited team and professional position reports`
        : t`${products} reports of positions, professionals and teams by month`,
    )
    const comparisons = plan.limitComparison ?? ''
    lines.push(
      plan.limitComparison === -1
        ? t`Free and unlimited comparison reports`
        : t`${comparisons} monthly comparison reports`,
    )
    const markets = plan.limitMarket ?? ''
    lines.push(
      plan.limitMarket === -1
        ? t`Free and unlimited market profile reports`
        : t`${markets} monthly market profile reports`,
    )
    return lines.map((line) => `• ${line}`).join('\n')
  }

  const planName = (name: string | null) => {
    return name?.includes('free')
      ? t`Free`
      : name?.includes('standard')
        ? t`Company`
        : name?.includes('plus')
          ? t`Integration`
          : t`Customized`
  }

  function getBillingStatusName(
    status: 'trialing' | 'active' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'canceled' | 'unpaid',
  ): string {
    switch (status) {
      case 'trialing':
        return t`proof`
      case 'active':
        return t`active`
      case 'incomplete':
      case 'incomplete_expired':
        return t`incomplete`
      case 'past_due':
        return t`defeated`
      case 'canceled':
        return t`cancelled`
      case 'unpaid':
        return t`not payed`
    }
  }

  return (
    <>
      {/* Current Plan Overview */}
      <SettingsSection title={t`Current plan`} description={t`Your subscription details and usage`}>
        <div className="flex flex-col gap-6">
          {/* Plan header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-primary/10 flex size-12 items-center justify-center rounded-xl">
                <CreditCardIcon className="text-primary size-6" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-lg font-semibold">{planName(customer.subscription.plan)}</h4>
                  {customer.subscription.plan !== 'free' && customer.subscription.status && (
                    <span
                      className={cn(
                        'rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize',
                        getStatusColor(customer.subscription.status, customer.subscription.planCanceled ?? undefined),
                      )}
                    >
                      {getBillingStatusName(
                        (customer.subscription.planCanceled ? 'canceled' : customer.subscription.status) as
                          | 'trialing'
                          | 'active'
                          | 'incomplete'
                          | 'incomplete_expired'
                          | 'past_due'
                          | 'canceled'
                          | 'unpaid',
                      )}
                    </span>
                  )}
                </div>
                {customer.subscription.plan !== 'free' && customer.subscription.price && (
                  <p className="text-muted-foreground text-sm">
                    {eurFormatter.format(customer.subscription.price / 100)} / {t`Month`}
                  </p>
                )}
              </div>
            </div>
            <Button
              onClick={() =>
                retrieveBillingPortal().then(({ customerPortalUrl }) => {
                  window.location.href = customerPortalUrl
                })
              }
              disabled={isPendingPortal}
              variant="outline"
            >
              {isPendingPortal && <Spinner className="size-4" />}
              {customer.subscription.plan === 'free' ? t`Go to the billing panel` : t`Manage subscription`}
              <ExternalLinkIcon className="size-4" />
            </Button>
          </div>

          {/* Usage stats */}
          <div className="grid gap-4 sm:grid-cols-3">
            <UsageStat
              icon={<TrendingUpIcon className="size-4" />}
              label={t`Reports consumed`}
              current={customer.subscription.product.count}
              limit={customer.subscription.product.limit}
            />
            <UsageStat
              icon={<TrendingUpIcon className="size-4" />}
              label={t`Consumed comparisons`}
              current={customer.subscription.comparison.count}
              limit={customer.subscription.comparison.limit}
            />
            <UsageStat
              icon={<TrendingUpIcon className="size-4" />}
              label={t`Market profiles`}
              current={customer.subscription.market.count}
              limit={customer.subscription.market.limit}
            />
          </div>

          {/* Billing period */}
          <div className="bg-muted/50 flex flex-wrap gap-6 rounded-lg p-4 text-sm">
            <div className="flex items-center gap-2">
              <CalendarIcon className="text-muted-foreground size-4" />
              <span className="text-muted-foreground">{t`Beginning of the plan`}:</span>
              <span className="font-medium">{formatDateLocale(customer.subscription.planStart, language)}</span>
            </div>
            <div className="flex items-center gap-2">
              <CalendarIcon className="text-muted-foreground size-4" />
              <span className="text-muted-foreground">{t`End of the plan`}:</span>
              <span className="font-medium">{formatDateLocale(customer.subscription.planEnd, language)}</span>
            </div>
          </div>
        </div>
      </SettingsSection>

      {/* Available Plans */}
      {customer.subscription.plan === 'free' && customer.plans.length > 0 && (
        <SettingsSection title={t`Plans available`} description={t`Upgrade your plan to unlock more features`}>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {customer.plans.map((plan) => (
              <div
                key={plan.id}
                role="button"
                tabIndex={0}
                onClick={() =>
                  retrieveBillingIntent({ productId: plan.id, productType: 'subscription' }).then(({ sessionUrl }) => {
                    window.location.href = sessionUrl
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    retrieveBillingIntent({ productId: plan.id, productType: 'subscription' }).then(
                      ({ sessionUrl }) => {
                        window.location.href = sessionUrl
                      },
                    )
                  }
                }}
                className={cn(
                  'group relative flex cursor-pointer flex-col gap-4 rounded-xl border-2 border-muted bg-card p-5 text-left transition-[border-color,box-shadow] hover:border-primary/50 hover:shadow-md',
                  isPending && 'pointer-events-none opacity-70',
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="flex flex-col gap-1">
                    <h4 className="font-semibold">{planName(plan.name)}</h4>
                    <p className="text-primary text-2xl font-bold">
                      {eurFormatter.format((plan.price ?? 0) / 100)}
                      <span className="text-muted-foreground text-sm font-normal"> / {t`Month`}</span>
                    </p>
                  </div>
                  <InfoPopover>{planDescription(plan)}</InfoPopover>
                </div>
                <div className="text-muted-foreground group-hover:text-primary mt-auto flex items-center gap-2 text-sm">
                  <span>{t`Select plan`}</span>
                  <CheckIcon className="size-4 opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
              </div>
            ))}
          </div>
        </SettingsSection>
      )}
    </>
  )
}

function BillingPlanSkeleton() {
  const { t } = useLingui()
  return (
    <SettingsSection title={t`Current plan`} description={t`Your subscription details and usage`}>
      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-4">
          <div className="bg-primary/10 flex size-12 items-center justify-center rounded-xl">
            <CreditCardIcon className="text-primary size-6" />
          </div>
          <div className="flex flex-col gap-1">
            <h4 className="text-lg font-semibold">
              <Skeleton chars={8} />
            </h4>
            <p className="text-muted-foreground text-sm">
              <Skeleton chars={10} />
            </p>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {[t`Reports consumed`, t`Consumed comparisons`, t`Market profiles`].map((label) => (
            <div key={label} className="bg-card flex flex-col gap-2 rounded-lg border p-4">
              <div className="text-muted-foreground flex items-center gap-2">
                <TrendingUpIcon className="size-4" />
                <span className="text-sm">{label}</span>
              </div>
              <Skeleton chars={6} />
            </div>
          ))}
        </div>
      </div>
    </SettingsSection>
  )
}

function UsageStat(props: { icon: React.ReactNode; label: string; current: number; limit: number }) {
  const percentage = props.limit === -1 ? 0 : props.limit > 0 ? Math.min((props.current / props.limit) * 100, 100) : 0
  const limitDisplay = props.limit === -1 ? '∞' : props.limit

  return (
    <div className="bg-card flex flex-col gap-2 rounded-lg border p-4">
      <div className="text-muted-foreground flex items-center gap-2">
        {props.icon}
        <span className="text-sm">{props.label}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-bold">{props.current}</span>
        <span className="text-muted-foreground text-sm">/ {limitDisplay}</span>
      </div>
      {props.limit !== -1 && props.limit > 0 && (
        <div className="bg-muted h-1.5 overflow-hidden rounded-full">
          <div
            className={cn(
              'h-full rounded-full transition-[width]',
              percentage >= 90 ? 'bg-destructive' : percentage >= 70 ? 'bg-amber-500' : 'bg-primary',
            )}
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}
    </div>
  )
}
